import { v7 as uuidv7 } from "uuid";
import type { SdeWorkbookClient, SdeWorkbookClientError } from "@services/clients/SdeWorkbookClient";
import { SdeWorkbookRoutes } from "@services/clients/SdeWorkbookRoutes";
import type { TaskEitherTs } from "@ps-bridge/interop";
import { taskEitherChain, taskEitherLeft, taskEitherTryCatch } from "@utils/effects/branded";
import API_URL from "@utils/config";

const networkError = (): SdeWorkbookClientError => ({
    type: "NetworkError",
    message: "The SDE workbook service could not be reached. Try again when the service is available.",
});

const responseDecodeError = (): SdeWorkbookClientError => ({
    type: "ResponseDecodeError",
    message: "The SDE workbook response could not be read.",
});

const errorFromStatus = (status: number, message: string): SdeWorkbookClientError => ({
    type: "BackendError",
    status,
    message: message.trim() === "" ? `SDE workbook generation failed with HTTP ${status}.` : message,
});

export class DefaultSdeWorkbookClient implements SdeWorkbookClient {
    generateWorkbook(file: File): TaskEitherTs<SdeWorkbookClientError, Blob> {
        const fetchTask = taskEitherTryCatch(
            () =>
                fetch(`${API_URL}${SdeWorkbookRoutes.generateWorkbook}`, {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/pdf",
                        "X-Trace-ID": uuidv7(),
                        "X-Upload-Filename": file.name,
                    },
                    body: file,
                }),
            () => networkError(),
        );

        return taskEitherChain(fetchTask, (response) => {
            if (response.ok) {
                return taskEitherTryCatch(
                    () => response.blob(),
                    () => responseDecodeError(),
                );
            }

            const messageTask = taskEitherTryCatch(
                () => response.text(),
                () => errorFromStatus(response.status, "Response body unavailable"),
            );

            return taskEitherChain(messageTask, (message) =>
                taskEitherLeft(errorFromStatus(response.status, message || response.statusText)),
            );
        });
    }
}
